import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Calendar, 
  Clock, 
  User, 
  Eye, 
  Heart, 
  MessageCircle,
  ArrowRight,
  Tag
} from 'lucide-react';
import { Link } from 'react-router-dom';

const BlogCard = ({ article, featured = false }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Artificial Intelligence': 'bg-blue-500',
      'CRM & Sales': 'bg-green-500',
      'Digital Transformation': 'bg-purple-500',
      'Automation': 'bg-orange-500',
      'Technology': 'bg-indigo-500',
      'Business Strategy': 'bg-red-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  return (
    <Card className={`group hover:shadow-xl transition-all duration-300 border-border/50 hover:border-primary/30 overflow-hidden ${
      featured ? 'lg:col-span-2' : ''
    }`}>
      <Link to={`/blog/${article.id}`}>
        <CardContent className="p-0">
          {/* Featured Image */}
          <div className={`relative overflow-hidden ${featured ? 'h-64' : 'h-48'}`}>
            <img 
              src={article.featuredImage} 
              alt={article.title}
              className="w-full h-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-primary/5 hidden">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Tag className="h-8 w-8 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">Featured Image</p>
                </div>
              </div>
            </div>
            
            {/* Category Badge */}
            <div className="absolute top-4 left-4">
              <Badge 
                className={`${getCategoryColor(article.category)} text-white border-0`}
              >
                {article.category}
              </Badge>
            </div>

            {/* Featured Badge */}
            {featured && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-yellow-500 text-white border-0">
                  Featured
                </Badge>
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Title */}
            <h3 className={`font-bold text-foreground mb-3 group-hover:text-primary transition-colors ${
              featured ? 'text-2xl' : 'text-xl'
            }`}>
              {article.title}
            </h3>

            {/* Excerpt */}
            <p className="text-muted-foreground mb-4 leading-relaxed line-clamp-3">
              {article.excerpt}
            </p>

            {/* Author and Meta Info */}
            <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4" />
                <span>{article.author.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                <span>{formatDate(article.publishDate)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{article.readTime}</span>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center gap-4 mb-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-1">
                <Eye className="h-4 w-4" />
                <span>{article.views.toLocaleString()}</span>
              </div>
              <div className="flex items-center gap-1">
                <Heart className="h-4 w-4" />
                <span>{article.likes}</span>
              </div>
              <div className="flex items-center gap-1">
                <MessageCircle className="h-4 w-4" />
                <span>{article.comments}</span>
              </div>
            </div>

            {/* Tags */}
            <div className="flex flex-wrap gap-2 mb-4">
              {article.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {tag}
                </Badge>
              ))}
              {article.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{article.tags.length - 3} more
                </Badge>
              )}
            </div>

            {/* Read More */}
            <div className="flex items-center gap-2 text-primary font-medium group-hover:gap-3 transition-all">
              <span>Read More</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </div>
          </div>
        </CardContent>
      </Link>
    </Card>
  );
};

export default BlogCard;
